<template>
    <m-pageframe
        title="Preview Recipients"
        :color="pageframe_color"
    >
        <v-form>
            <v-container
                fluid
                style="padding-left: 30px; padding-right: 30px"
            >
                <v-alert
                    type="info"
                    v-if="is_canadian_organization && used_phone_validation"
                >
                    For Canadian phone numbers, text message eligibility cannot be verified in advance. The number will be treated as valid, but the first message may be rejected by the carrier if the number cannot receive texts. 
                </v-alert>

                <v-alert
                    type="error"
                    color="primary"
                    v-if="above_warn_change_contact_threshold"
                >
                    <span
                        v-if="pretty_percent_updated_contacts.startsWith('100')"
                    >
                        Warning: This import would <b><u>change</u></b> the names of a large number of <b><u>pre-existing contacts</u></b>.  <b><u>Most names would be altered.</u></b>
                    </span>
                    <span
                        v-else
                    >
                        Warning: This import would <b><u>change</u></b> the names of a large number of <b><u>pre-existing contacts</u></b>.  The names of more than <b><u>{{pretty_percent_updated_contacts}}</u></b> would be altered.
                    </span>
                </v-alert>

                <v-layout>
                    <v-flex flex-grow-0>
                        <h2>Valid Rows ({{valid_recipients_total}})</h2>
                    </v-flex>

                    <v-flex
                        flex-grow-1
                        align-self-center
                        style="text-align: right; padding-bottom: 15px"
                    >
                        <v-menu 
                            bottom 
                            offset-y  
                        >
                        
                        <template v-slot:activator="{ on }">
                                
                            <v-icon 
                                v-on="on"
                            >
                                mdi-dots-vertical
                            </v-icon>
                                
                        </template>
                        
                        <v-list>
                            <v-list-item
                                v-on:click="checkAll()"
                            >
                                <v-list-item-title>Check All</v-list-item-title>
                            </v-list-item>

                            <v-list-item
                                v-on:click="uncheckAll()"
                            >
                                <v-list-item-title>Uncheck All</v-list-item-title>
                            </v-list-item>

                            <v-divider/>

                            <v-list-item
                                v-on:click="exportToExcel()"
                            >
                                <v-list-item-title>Export to Excel...</v-list-item-title>
                            </v-list-item>
                        
                        </v-list>
                        
                        </v-menu> 
                    </v-flex>
                </v-layout>

                <v-data-table
                    :headers="valid_intermediate_recipient_headers"
                    :items="valid_recipients"
                    :items-per-page="25"
                    class="elevation-1"
                    :loading="!this.recipients"
                    loading-text="Loading... Please wait"
                    :footer-props="{
                        itemsPerPageOptions: [25, 50, 100, -1],
                    }"
                >
                    <template v-slot:item.should_include="{ item }">
                        <v-simple-checkbox v-ripple="false" v-model="item.rc_include"></v-simple-checkbox>
                    </template>

                    <template v-slot:item.pretty_phone_number="{ item }">
                        {{prettyPrintPhoneNumber(item)}}
                    </template>

                    <template v-slot:item.action="{ item }">
                        <v-menu
                            bottom
                            offset-y
                        >

                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-dots-vertical-circle-outline
                                </v-icon>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-on:click="onClickEditRecipient(item)"
                                >
                                    <v-list-item-title>Edit</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickDeleteRecipient(item)"
                                >
                                    <v-list-item-title>Delete</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>

                </v-data-table>
            </v-container>

            <v-container
                v-if="invalid_recipients.length > 0"
            >
                <v-layout>
                    <v-flex flex-grow-0>
                        <h2>Invalid Rows ({{invalid_recipients_total}})</h2>
                    </v-flex>

                    <v-flex
                        flex-grow-1
                        align-self-center
                        style="text-align: right; padding-bottom: 15px"
                    >
                        <v-btn
                            v-on:click="exportToExcel"
                        >
                            Export to Excel
                        </v-btn>
                    </v-flex>
                </v-layout>

                <v-data-table
                    :headers="invalid_intermediate_recipient_headers"
                    :items="invalid_recipients"
                    :items-per-page="25"
                    class="elevation-1"
                    :footer-props="{
                        itemsPerPageOptions: [25, 50, 100, -1],
                    }"
                >
                    <template v-slot:item.should_include="{ item }">
                        <v-simple-checkbox v-ripple="false" v-model="item.rc_include"></v-simple-checkbox>
                    </template>

                    <template v-slot:item.pretty_phone_number="{ item }">
                        {{prettyPrintPhoneNumber(item)}}
                    </template>

                    <template v-slot:item.action="{ item }">
                        <v-menu
                            bottom
                            offset-y
                        >

                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-dots-vertical-circle-outline
                                </v-icon>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-on:click="onClickEditRecipient(item)"
                                >
                                    <v-list-item-title>Edit</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-on:click="onClickDeleteRecipient(item)"
                                >
                                    <v-list-item-title>Delete</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>

                </v-data-table>
            </v-container>

            <v-container
                v-if="deleted_recipients.length > 0"
            >
                <v-layout>
                    <h2>Deleted Rows ({{deleted_recipients_total}})</h2>
                </v-layout>

                <v-data-table
                    :headers="deleted_recipient_headers"
                    :items="deleted_recipients"
                    :items-per-page="25"
                    class="elevation-1"
                    :footer-props="{
                        itemsPerPageOptions: [25, 50, 100, -1],
                    }"
                >
                    <template v-slot:item.should_include="{ item }">
                        <v-simple-checkbox v-ripple="false" v-model="item.rc_include"></v-simple-checkbox>
                    </template>

                    <template v-slot:item.pretty_phone_number="{ item }">
                        {{prettyPrintPhoneNumber(item)}}
                    </template>

                    <template v-slot:item.action="{ item }">
                        <v-menu
                            bottom
                            offset-y
                        >

                            <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-dots-vertical-circle-outline
                                </v-icon>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-on:click="onClickRestore(item)"
                                >
                                    <v-list-item-title>Restore</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>

                </v-data-table>
            </v-container>

            <v-container>
                <div>
                    <v-btn
                        color="secondary"
                        style="margin-bottom: 15px; margin-top: 15px; margin-right: 15px"
                        v-on:click="onClickCancel"
                    >
                        Cancel Import
                    </v-btn>
                    <v-btn
                        color="secondary"
                        style="margin-bottom: 15px; margin-top: 15px; margin-right: 15px"
                        v-on:click="onClickValidate"
                    >
                        Validate Phone Numbers
                    </v-btn>
                    <v-btn
                        color="primary"
                        style="margin-bottom: 15px; margin-top: 15px"
                        v-on:click="onClickUpload"
                    >
                        Upload Recipients
                    </v-btn>
                </div>
            </v-container>
        </v-form>

        <m-bulk-intermediate-recipient-edit-dialog
            ref="edit-bulk-recipient"
            v-on:ok="upsertRecipientPatch"
        />
    </m-pageframe>
</template>

<script>

const E164 = require("@/js/E164.js");
const { v4: uuidv4 } = require('uuid');

export default {
    data() {
        return {
            organization_id: null,
            bulk_message_id: null,
            import_id: null,
            
            recipients: [],
            valid_recipients: [],
            invalid_recipients: [],
            deleted_recipients: [],


            headers: [],
            sheet_name: null,
            used_phone_validation: false,

            rules: {
                required: (value) => { if ( value == null || value.trim().length == 0 ) return 'Required'; return true;  },
            },
        }
    },

    created () {
        this.$event_bus.$on('common-bulk-message-import-progress', this.updateProgress);
    },

    beforeDestroy () {
        this.$event_bus.$off('common-bulk-message-import-progress',this.updateProgress);
    },

    mounted() {
        this.organization_id = this.$route.params.organization_id;
        this.bulk_message_id = this.$route.params.bulk_message_id;
        this.import_id = this.$route.params.import_id;

        this.getBulkRecipientImport();
    },

    methods: {

        updateProgress(signal)
        {
            if ( signal.bulk_import_id != this.last_signal.bulk_import_id ) return;
            this.last_signal = signal;

            if ( signal.progress == 'finished' )
            {
                this.$rc.hideSpinner();

                switch (signal.type)
                {
                    case "recipient-validation":
                    {
                        this.used_phone_validation = true;
                        // retrieve data again
                        this.getBulkRecipientImport();
                        break;
                    }
                    case "commit-worksheet":
                    default:
                    {
                        // no longer done using signals...
                        break;
                    }
                }
            }

            // check if recipient validation took too long
            if ( signal.progress == 'failed' && signal.type == 'recipient-validation' )
            {
                this.$rc.hideSpinner();
                this.handlePhoneValidationFailure(signal);
            }
        },

        handlePhoneValidationFailure(signal)
        {
            this.$rc.confirm(
                'Phone Validation Incomplete',
                'Validation is taking longer than usual for some phone numbers. '
                + signal.additional_data.total_pending + ' phone numbers have not yet been validated.',
                [
                    {'text' : 'Keep Trying', 'value' : 'keep_trying' },
                    {'text' : 'Exclude Unvalidated', 'value' : 'exclude_unvalidated' },
                    {'text' : 'Include Unvalidated', 'value' : 'include_unvalidated' },
                ],
                (response) => {
                    switch (response) {
                        case 'keep_trying':
                        {
                            this.$rc.updateContinueBulkRecipientEndpointValidation(
                                this.organization_id,
                                this.import_id,
                                signal.additional_data.batch_id,
                            );

                            break;
                        }
                        case 'exclude_unvalidated':
                        {
                            this.$rc.updateForceBulkRecipientNumberValidation(
                                this.organization_id,
                                this.import_id,
                                true,
                                (data) => {
                                    this.getBulkRecipientImport();
                                },
                            );

                            break;
                        }
                        case 'include_unvalidated':
                        {
                            this.$rc.updateForceBulkRecipientNumberValidation(
                                this.organization_id,
                                this.import_id,
                                false,
                                (data) => {
                                    this.getBulkRecipientImport();
                                },
                            );

                            break;
                        }
                    }
                }
            );
        },

        onClickValidate()
        {
            this.$rc.showSpinner('Validating phone numbers, this can take several minutes...');

            this.$rc.updateBulkRecipientPhoneNumberValidationBandwidth(
                this.organization_id,
                this.import_id,
                (data) => {
                    if ( !data.success )
                    {
                        this.$rc.hideSpinner();

                        this.$rc.alert(
                            'Error Uploading File',
                            'There was an error uploading the recipients file, please try again',
                        );
                    }
                    else
                    {
                        this.last_signal = data.signal;
                    }
                }
            );
        },

        onClickUpload()
        {
            if ( !this.above_warn_change_contact_threshold )
            {
                this.doUpload();
                return;
            }
            else
            {

                
                 this.$rc.confirm({
                    'title': 'Confirm High Percentage of Name Changes',
                    'body': 'Proceeding with this import would change the names of many pre-existing contacts.  More than <b><u>'+this.pretty_percent_updated_contacts+'</u></b> of recipients would have their name changed.<br/><br/><b><u>This frequently happens when the imported file contains an error.</u></b>  For example, the names and phone numbers are mis-aligned in your spreadsheet (even by one row).<br/><br/>Please check that your import does not have a problem <b><u>very carefully</u></b> before proceeding.',
                    'btn_text_ok': 'Proceed',
                    'btn_text_cancel': 'Cancel',
                    'text_to_confirm_message': 'To proceed, type "proceed" below.',
                    'text_to_confirm': 'proceed',
                    'on_ok': () => {
                        this.doUpload();
                    },
                });
            }
        },

        doUpload()
        {
            this.$rc.showSpinner('Creating recipient information, this can take several minutes...');

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/recipients/async-commit-worksheet/start',
                'timeout': 30*60*1000, // 30 minute timeout
            
                'params' : {
                        'organization_id': this.organization_id,
                        'bulk_message_id': this.bulk_message_id,
                        'import_id': this.import_id, 
                        'worksheet': this.generateWorksheet(),
                    },
            
                'on_success': (data) => {
                        this.onCheckUpdateCommit(data.process_id, 601);
                    },
            
                'on_error': () => {
                    this.$rc.hideSpinner();

                    this.alert(
                        'Unable to Upload Recipients',
                        'Unable to upload recipients. Please try upload again.'
                    );
                },
            });
        },

        onCheckUpdateCommit(process_id, checks_remaining)
        {
            if ( checks_remaining < 0 )
            {
                this.$rc.hideSpinner();
                this.$rc.alert('Commit recipients failed', 'Unable to commit your recipients.  Please try again');
                return;
            }

            checks_remaining--;

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/async-process/:process_id',
            
                'params' : {
                        'process_id': process_id,
                    },
            
                'on_success': (data) => {
                        if ( data.state == 'finished' )
                        {
                            this.$router.push({
                                'name': 'view-bulk-message',
                                'params' : {
                                    'organization_id' : this.organization_id,
                                    'bulk_message_id' : this.bulk_message_id,
                                }
                            }).catch(e=>{}); // swallow any exception

                            return;
                        }
                        else
                        {
                            // Still running...
                            window.setTimeout(
                                () => {
                                    this.onCheckUpdateCommit(process_id, checks_remaining);
                                },
                                1000
                            )
                        }
                    },
            
                'on_error': () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Unable to commit recipients','Unable to commit recipients');
                },
            });
        },

        onClickCancel()
        {
            this.$router.push({
                'name': 'view-bulk-message',
                'params' : {
                    'organization_id' : this.organization_id,
                    'bulk_message_id' : this.bulk_message_id,
                }
            }).catch(e=>{}); // swallow any exception
        },

        upsertRecipientPatch(new_recipient)
        {
            for ( let i=0; i < this.recipients.length; i++ )
            {
                if ( this.recipients[i].rc_row_number == new_recipient.rc_row_number )
                {
                    this.recipients[i] = new_recipient;
                    break;
                }
            }

            this.upsertRecipients();
        },

        getBulkRecipientImport()
        {
            this.$rc.showSpinner();

            this.$rc.getBulkRecipientImport(
                this.organization_id,
                this.import_id,
                (data) => {
                    this.$rc.hideSpinner();
                    this.recipients = data.results.intermediate_sheet.data;

                    this.valid_recipients = this.filterRecipients('valid');
                    this.invalid_recipients = this.filterRecipients('invalid');
                    this.deleted_recipients = this.filterRecipients('deleted');

                    this.headers = data.results.intermediate_sheet.headers;
                    this.sheet_name = data.results.intermediate_sheet.text;
                },
                () => {
                    this.$rc.alert(
                        'Error retrieving Excel Import',
                        'There was an error retrieving the uploaded Excel file. Please try again.'
                    );
                },
            );
        },

        exportToExcel()
        {
            this.$rc.startExcelExportBulkPreviewRecipients(
                this.organization_id,
                this.import_id,
                (data) => {
                    window.open(data.url, "_blank");
                },
                () => {
                    this.$rc.alert(
                        "Error with Excel Export",
                        "There was an error with the Excel Export, please try again."
                    );
                }
            );
        },

        prettyPrintPhoneNumber(item)
        {
            return E164.prettyPrintUsPhoneNumber(item.rc_phone_number);
        },

        generateWorksheet()
        {
            let ret = {
                'data' : this.recipients,
                'headers' : this.headers,
                'text' : this.sheet_name,
            };

            return ret;
        },

        onClickRestore(recipient)
        {
            this.$rc.confirm(
                'Restore Recipient',
                'Are you sure you want to restore this recipient?',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Confirm', 'value': 'confirm'},
                ],
                (response) => {

                    if (response == 'confirm') {
                        recipient.rc_is_deleted = false;
                        this.upsertRecipients();
                    } else {
                        return;
                    }
                }
            );
        },

        onClickDeleteRecipient(recipient)
        {
            this.$rc.confirm(
                'Delete Recipient',
                'Are you sure you want to delete this recipient?',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Confirm', 'value': 'confirm'},
                ],
                (response) => {

                    if (response == 'confirm') {
                        recipient.rc_is_deleted = true;
                        this.upsertRecipients();
                    } else {
                        return;
                    }
                }
            );
        },

        upsertRecipients()
        {
            this.$rc.upsertRecipientsExcelWorksheet(
                this.organization_id,
                this.import_id,
                this.generateWorksheet(),
                (data) => {
                    this.getBulkRecipientImport();
                },
                () => {
                    this.alert(
                        'Error Updating Recipients',
                        'There was an error updating the recipients, please try again.'
                    );
                },
            );
        },

        getExtraFields() {

            let ret = {};

            for ( let i=0; i < this.headers.length; i++ )
            {
                let node = this.headers[i];

                // omit reserved column names
                if ( this.isReservedColumnName(node.value) ) continue;

                ret[node.value] = node.text;
            }

            return ret;
        },

        onClickEditRecipient(item) {

            let extra_fields = this.getExtraFields();

            this.$refs['edit-bulk-recipient'].showDialog(
                this.organization_id,
                extra_fields,
                item,
            );
        },

        intermediate_recipient_headers(valid, show_include_checkbox) {
            if ( this.headers == null || this.headers == [] ) return [];

            let ret = [];

            if ( show_include_checkbox )
            {
                ret.push({ text: 'Include', value: 'should_include'});
            }

            ret.push({ text: 'Row Number', value: 'rc_row_number'});
            ret.push({ text: 'Validation Status', value: 'rc_validation_status'});

            if ( valid )
            {
                ret.push({ text: 'Phone Number', value: 'pretty_phone_number'});
            }
            else
            {
                ret.push({ text: 'Phone Number', value: 'rc_phone_number'});
            }

            ret.push({ text: 'First Name', value: 'rc_first_name'});
            ret.push({ text: 'Last Name', value: 'rc_last_name'});

            for ( let i=0; i < this.headers.length; i++ )
            {
                // omit reserved column names
                if ( this.isReservedColumnName(this.headers[i].value) ) continue;

                ret.push(this.headers[i]);
            }

            ret.push({ text: 'Notes', value: 'rc_system_notes'});
            ret.push({ text: 'Action', value: 'action'});

            return ret;
        },

        isReservedColumnName(value)
        {
            if ( value == 'rc_phone_number' ) return true;
            if ( value == 'rc_first_name' ) return true;
            if ( value == 'rc_last_name' ) return true;
            if ( value == 'rc_is_valid' ) return true;
            if ( value == 'rc_create_contact' ) return true;
            if ( value == 'rc_update_contact' ) return true;
            if ( value == 'rc_system_notes' ) return true;
            if ( value == 'rc_include' ) return true;
            if ( value == 'rc_is_deleted' ) return true;
            if ( value == 'rc_row_number' ) return true;
            if ( value == 'rc_validation_status' ) return true;
        },

        filterRecipients(type)
        {
            let ret = [];

            if ( !this.recipients )
            {
                return ret;
            }

            for ( let i = 0; i < this.recipients.length; i++ ) {
                let recipient = this.recipients[i];

                let external_validation_status = !recipient.rc_validation_status || recipient.rc_validation_status == '' || recipient.rc_validation_status == 'Valid';

                switch (type)
                {
                    case 'invalid':
                    {
                        if ( (!recipient.rc_is_valid || !external_validation_status) && !recipient.rc_is_deleted )
                        {
                            ret.push(recipient);
                        }

                        break;
                    }
                    case 'deleted':
                    {
                        if ( recipient.rc_is_deleted )
                        {
                            ret.push(recipient);
                        }

                        break;
                    }
                    case 'valid':
                    default:
                    {
                        if ( recipient.rc_is_valid && !recipient.rc_is_deleted && external_validation_status )
                        {
                            ret.push(recipient);
                        }

                        break;
                    }
                }
            }

            return ret;
        },

        checkAll()
        {
            this.setAllShouldInclude(true);
        },

        uncheckAll()
        {
            this.setAllShouldInclude(false);
        },

        setAllShouldInclude(value)
        {
            for ( let i = 0; i < this.valid_recipients.length; i++ )
            {
                this.valid_recipients[i].rc_include = value;
            }
        },

    },
    computed: {
        
        is_canadian_organization()
        {
            return this.$rc.$state?.profile?.organization_account?.country_and_subdivision?.startsWith('CA');
        },

        valid_intermediate_recipient_headers() {
            return this.intermediate_recipient_headers(true, true);
        },

        invalid_intermediate_recipient_headers() {
            return this.intermediate_recipient_headers(false, false);
        },

        deleted_recipient_headers() {
            return this.intermediate_recipient_headers(true, false);
        },

        valid_recipients_total() {
            return this.valid_recipients.length;
        },

        invalid_recipients_total() {
            return this.invalid_recipients.length;
        },

        deleted_recipients_total() {
            return this.deleted_recipients.length;
        },

        pageframe_color() {
            if ( this.$route.params.organization_id !== this.$rc.$state.organization_id )
                return '#5D647C';
            else
                return '#F24653';
        },

        percent_updated_contacts()
        {
            let recipients = this.recipients;

            if ( recipients == null || recipients.length == 0 ) return 0;

            let count_updated = 0;
            for ( let i = 0; i < recipients.length; i++ )
            {
                if ( recipients[i].rc_update_contact == true )
                {
                    count_updated++;
                }
            }

            return count_updated / recipients.length;
        },

        pretty_percent_updated_contacts()
        {
            return (this.percent_updated_contacts*100).toFixed(1)+'%';
        },

        above_warn_change_contact_threshold()
        {
            return this.percent_updated_contacts > 0.05;
        },
    },
}
</script>

<style lang="scss" scoped>
</style>